import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import DownloadGateModal from "../components/DownloadGateModal";

const DownloadContext = createContext(null);

// Files on our storage download in place; outside links open in a new tab.
// No tab is opened until the link is ready, so nothing blank ever flashes up.
function deliver(pack, url) {
  if (pack.is_hosted) {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  const opened = window.open(url, "_blank");
  if (!opened) {
    // Some browsers (Safari) block a tab opened after the request finished:
    // let the visitor open it with one more click instead of leaving the site.
    toast.success("Your download is ready", {
      duration: 20000,
      action: { label: "Open", onClick: () => window.open(url, "_blank") },
    });
  }
}

function explain(err) {
  const d = err.response?.data;
  if (d?.code === "limit_reached" || d?.code === "members_only") {
    toast.error(d.detail, {
      duration: 9000,
      action: { label: "See AA+", onClick: () => { window.location.href = "/aa-plus"; } },
    });
  } else {
    toast.error(d?.detail || "Could not start the download");
  }
}

/**
 * `requestDownload(pack, { owner })` starts a scenepack download. AA+ members, admins and
 * scenepack creators get the file straight away; everyone else first sees the
 * sponsor gate. Resolves with { remaining, limit } once the download started,
 * or null if it was refused or cancelled.
 *
 * `owner: "drawer"` means the gate is shown by the character drawer itself (see
 * DownloadGateModal for why); otherwise this provider shows it.
 */
export function DownloadProvider({ children }) {
  const [gate, setGate] = useState(null); // { pack, ad, ticket, minSeconds, owner }
  const resolver = useRef(null);

  const finish = useCallback((result) => {
    resolver.current?.(result);
    resolver.current = null;
    setGate(null);
  }, []);

  const requestDownload = useCallback((pack, { owner = "page" } = {}) => new Promise((resolve) => {
    (async () => {
      try {
        const { data: start } = await api.post(`/scenepacks/${pack.id}/download/start`);
        if (start.ad_required) {
          resolver.current = resolve;
          setGate({ pack, ad: start.ad, ticket: start.ticket, minSeconds: start.min_seconds, owner });
          return;
        }
        const { data } = await api.post(`/scenepacks/${pack.id}/download`, {});
        deliver(pack, data.url);
        resolve(data);
      } catch (err) {
        explain(err);
        resolve(null);
      }
    })();
  }), []);

  // Called by the gate's unlocked Download button.
  const downloadFromGate = async () => {
    const { pack, ticket } = gate;
    try {
      const { data } = await api.post(`/scenepacks/${pack.id}/download`, { ticket });
      deliver(pack, data.url);
      finish(data);
    } catch (err) {
      explain(err);
      if (["limit_reached", "members_only"].includes(err.response?.data?.code)) finish(null);
    }
  };

  useEffect(() => {
    if (!gate) return undefined;
    const onKey = (e) => { if (e.key === "Escape") finish(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.dataset.overlay = "1"; // keeps the bottom player above the gate
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      delete document.body.dataset.overlay;
    };
  }, [gate, finish]);

  const closeGate = useCallback(() => finish(null), [finish]);

  return (
    <DownloadContext.Provider value={{ requestDownload, gate, downloadFromGate, closeGate }}>
      {children}
      {gate && gate.owner !== "drawer" && <DownloadGateModal gate={gate} onDownload={downloadFromGate} onClose={closeGate} />}
    </DownloadContext.Provider>
  );
}

export const useDownload = () => useContext(DownloadContext);
